'use client';

import React, { useEffect, useRef } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import PageFooter from '../../components/shared/PageFooter';
import {
  COMPARISON,
  SPLITWISE_STRENGTHS,
  TABSTER_DIFFERENCES,
  SOURCES,
  VERIFIED_ON,
} from './comparison';

const OMBRE = 'linear-gradient(135deg, #6ee7b7, #06dffa, #dcccfe)';

const SplitwiseAlternativeContent = () => {
  const font = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';
  const checkedOn = new Date(`${VERIFIED_ON}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });

  // Counting the concessions in the heading caps how bad the reader expects the
  // list to be — "Two things" promises a short list, "What Tabster doesn't do"
  // could be anything. Derived from the list so adding a third item can't leave
  // the heading claiming two.
  const COUNT_WORDS = ['', 'One thing', 'Two things', 'Three things', 'Four things'];
  const notDoHeading = COUNT_WORDS[SPLITWISE_STRENGTHS.length]
    ? `${COUNT_WORDS[SPLITWISE_STRENGTHS.length]} Tabster doesn’t do`
    : 'What Tabster doesn’t do';

  // Chapter marks. Timestamps are read off the cut of splitting-a-tab.mp4 —
  // the same frames the help centre's #t= deep links are calibrated against.
  // Re-check them if the clip is ever re-recorded.
  const BEATS = [
    { t: 0, at: '0:00', label: 'Receipt photographed' },
    { t: 12, at: '0:12', label: 'Shared to the group by link or QR' },
    { t: 23, at: '0:23', label: 'Everyone taps to claim what they ordered' },
    { t: 33, at: '0:33', label: 'Shared items swiped to split' },
    { t: 38, at: '0:38', label: 'Venmo request sent' },
  ];

  // Muted autoplay when the clip scrolls into view, same treatment as the help
  // centre. play() rejects under Low Power Mode and data saver — that's a fine
  // outcome (poster + controls are still there), so swallow it.
  const demoRef = useRef<HTMLVideoElement | null>(null);

  // Jump the clip to a chapter. Assigning currentTime is silently dropped while
  // the element has nothing seekable yet, and preload="none" means that is the
  // normal state until someone presses play — so a naive assignment here would
  // work only on rows clicked after the video had already been watched. Same
  // failure the help centre's #t= links hit. Retry on each event that can widen
  // the seekable range, and stop listening once it takes.
  const seekTo = (seconds: number) => {
    const el = demoRef.current;
    if (!el) return;
    const EVENTS = ['loadedmetadata', 'loadeddata', 'canplay', 'progress'];
    const apply = () => {
      const r = el.seekable;
      if (!r.length || r.end(r.length - 1) < seconds) return false;
      try { el.currentTime = seconds; } catch { return false; }
      return true;
    };
    const retry = () => {
      if (!apply()) return;
      EVENTS.forEach((e) => el.removeEventListener(e, retry));
    };
    if (!apply()) {
      EVENTS.forEach((e) => el.addEventListener(e, retry));
      if (el.readyState < 1) {
        el.preload = 'metadata';
        el.load();
      }
    }
    el.play().catch(() => {});
  };
  useEffect(() => {
    const el = demoRef.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else if (!el.paused) el.pause();
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <style>{`
        .cmp-wrap { max-width: 860px; margin: 0 auto; padding: 0 24px 64px; }
        /* Same lavender shimmer as the landing hero ("Split bills in seconds").
           Copied rather than imported because the keyframes and the class are
           declared inside LandingPage's own style block, which this page never
           mounts. Weight 600 + the Clash Display stack matches that h1 exactly
           — nothing declares @font-face for Clash, so both resolve to SF Pro
           Display; keeping the stack identical means they stay matched if the
           real font is ever added. */
        @keyframes landingSparkle {
          0%, 100% { background-position: 200% center; }
          50% { background-position: -200% center; }
        }
        .cmp-h1 {
          /* Same type scale as the About page hero (.about-mission-heading):
             27px, stepping to 32px at its 768px breakpoint. Keeps the two
             marketing pages on one scale instead of this one running larger and
             reading as a different site. */
          font-size: 27px; font-weight: 600; line-height: 1.3; letter-spacing: -0.02em;
          /* Top was 40px, which sat tight under the fixed header. */
          margin: 76px 0 16px;
          font-family: "Clash Display", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif;
          background: linear-gradient(90deg, #E8DEFF 0%, #E8DEFF 42%, #F5EEFF 50%, #E8DEFF 58%, #E8DEFF 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          animation: landingSparkle 6s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .cmp-h1 { animation: none; }
        }
        @media (min-width: 768px) {
          .cmp-h1 { font-size: 32px; line-height: 1.4; }
        }
        .cmp-lede { font-size: 17px; line-height: 1.65; color: rgba(255,255,255,0.72); margin: 0 0 14px; }
        .cmp-h2 {
          font-size: 22px; font-weight: 800; letter-spacing: -0.01em; color: white;
          margin: 44px 0 14px;
          font-family: var(--font-bricolage), -apple-system, BlinkMacSystemFont, sans-serif;
        }
        .cmp-p { font-size: 15.5px; line-height: 1.7; color: rgba(255,255,255,0.7); margin: 0 0 12px; }

        /* The table is the reason people are on this page, so it has to stay
           readable on a phone. Wrapping it in its own scroll container keeps
           the page body from scrolling sideways. */
        .cmp-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 18px 0 8px; }
        .cmp-table { width: 100%; border-collapse: collapse; min-width: 520px; font-size: 15px; }
        .cmp-table th, .cmp-table td {
          text-align: left; padding: 13px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .cmp-table thead th {
          font-size: 13px; text-transform: uppercase; letter-spacing: 0.06em;
          color: rgba(255,255,255,0.5); font-weight: 700;
          border-bottom: 1px solid rgba(255,255,255,0.16);
        }
        .cmp-table td { color: rgba(255,255,255,0.72); }
        .cmp-table td:first-child { color: white; font-weight: 600; }
        .cmp-row-hi { background: rgba(110,231,183,0.05); }
        /* Every "Free" cell gets a tick in the app's cyan ombre. The tick is a
           ::before rather than part of the data so comparison.ts stays plain
           text — it's also read out by screen readers as part of the cell,
           which is what you want here ("check, Free"). */
        .cmp-yes {
          font-weight: 700;
          background: linear-gradient(135deg, #6ee7b7, #06dffa, #dcccfe);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }
        .cmp-yes::before { content: '✓ '; }

        .cmp-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 18px; padding: 20px 22px; backdrop-filter: blur(12px);
        }
        /* Copy left, clip right once there's room — DOM order is copy then
           video, so the phone stack (text above, player below) falls out of
           the source order with no CSS order juggling. The 9:16 source is
           only 240px wide, which left a dead column beside it when the two
           were stacked full-width.
           NB: no backticks anywhere in this block — it is a template literal,
           and one backtick in a comment terminates the whole style string. */
        .cmp-demo { display: flex; flex-direction: column; gap: 18px; margin: 20px 0 6px; }
        @media (min-width: 720px) {
          .cmp-demo { flex-direction: row; align-items: center; gap: 30px; }
        }
        /* min-width:0 so the copy can shrink rather than forcing the row wider
           than the column — the default min-width:auto on a flex item refuses
           to go below its content's intrinsic width. */
        .cmp-demo-copy { min-width: 0; }
        .cmp-demo-video {
          display: block; width: 240px; max-width: 100%; height: auto;
          /* Reserves the box before metadata loads so the cards beneath it
             never get shoved down (CLS). */
          aspect-ratio: 1080 / 1920;
          flex-shrink: 0;
          border-radius: 16px; background: #000;
          border: 1px solid rgba(255,255,255,0.1);
        }
        /* The section heading lives inside the flex item here, so its usual
           44px top margin would push the whole row off its own heading. */
        .cmp-demo-copy .cmp-h2 { margin-top: 0; }
        .cmp-demo-copy p { margin: 0; font-size: 15px; line-height: 1.7; color: rgba(255,255,255,0.7); }
        /* Beat list — what happens when, so this column carries enough height
           to sit against a 9:16 clip instead of floating in the middle of it. */
        .cmp-beats { list-style: none; margin: 14px 0 0; padding: 0; }
        .cmp-beats li { border-top: 1px solid rgba(255,255,255,0.07); }
        .cmp-beat {
          display: flex; gap: 10px; align-items: baseline; width: 100%;
          background: none; border: 0; margin: 0; padding: 9px 0;
          font-family: inherit; font-size: 14.5px; line-height: 1.6;
          color: rgba(255,255,255,0.72);
          text-align: left; cursor: pointer;
          transition: color 0.15s ease;
        }
        .cmp-beat:hover { color: white; }
        .cmp-beat:focus-visible { outline: 2px solid #06dffa; outline-offset: 2px; border-radius: 6px; }
        .cmp-beat-time {
          flex-shrink: 0; min-width: 34px;
          font-variant-numeric: tabular-nums; font-weight: 700;
          background: linear-gradient(135deg, #6ee7b7, #06dffa, #dcccfe);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }

        .cmp-grid { display: grid; grid-template-columns: 1fr; gap: 14px; margin-top: 18px; }
        @media (min-width: 720px) { .cmp-grid { grid-template-columns: 1fr 1fr; } }
        /* flex + baseline keeps a two-line title aligned to the emoji rather
           than wrapping underneath it, and the fixed column means the titles
           all start at the same x however wide the glyph renders. */
        .cmp-card h3 {
          margin: 0 0 7px; font-size: 16px; font-weight: 800; color: white;
          display: flex; align-items: baseline; gap: 9px;
        }
        .cmp-card-emoji { flex-shrink: 0; width: 20px; font-size: 15px; }
        .cmp-card p { margin: 0; font-size: 14.5px; line-height: 1.65; color: rgba(255,255,255,0.68); }

        .cmp-cta { display: flex; flex-wrap: wrap; gap: 12px; margin: 22px 0 8px; }
        /* Rectangular rather than a pill — 10px is the radius the app already
           uses for its square-ish controls (see the Share Link button and the
           payment-method selector in UploadFlow), so these read as the same
           family rather than a third button shape invented for this page. */
        .cmp-btn {
          position: relative;
          display: inline-block; padding: 14px 24px; border-radius: 10px;
          font-weight: 800; font-size: 15px; text-decoration: none;
          letter-spacing: -0.01em;
          color: #06203a;
          /* Fill at 80%, so the page reads faintly through it. */
          background: linear-gradient(135deg,
            rgba(110,231,183,0.8), rgba(6,223,250,0.8), rgba(220,204,254,0.8));
          /* Transparent border so the filled button matches the outlined one's
             box exactly — without it the 1px border on .cmp-btn-alt made the
             two sit 2px apart in height, side by side. */
          border: 1px solid transparent;
        }
        /* The edge stays fully opaque while the fill is translucent, which a
           plain border can't do (a border takes a flat colour, not a gradient)
           and layered padding-box/border-box backgrounds can't either — the
           opaque layer would show straight through the 80% one and the fill
           would come back to full strength. This masks a 1px gradient ring:
           paint the solid gradient, then knock out everything inside the 1px
           padding so only the ring survives. inset:-1px puts it on the
           border-box edge rather than 1px in. */
        .cmp-btn::after {
          content: '';
          position: absolute;
          inset: -1px;
          border-radius: 10px;
          padding: 1px;
          background: ${OMBRE};
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask-composite: exclude;
          pointer-events: none;
        }
        /* The outlined variant draws its own edge; no ring. */
        .cmp-btn-alt::after { display: none; }

        /* Inline prose links in the app's cyan ombre. Underline needs its own
           colour — it inherits the color property, which is transparent under a
           clipped gradient, so it would otherwise be invisible. */
        .cmp-link {
          font-weight: 700; text-decoration: none;
          background: ${OMBRE};
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }
        .cmp-link:hover {
          text-decoration: underline;
          text-decoration-color: #06dffa;
          text-underline-offset: 3px;
        }
        .cmp-btn-alt {
          background: transparent; color: white;
          border: 1px solid rgba(255,255,255,0.22);
        }
        .cmp-sources {
          margin-top: 40px; padding-top: 18px;
          border-top: 1px solid rgba(255,255,255,0.08);
          font-size: 13px; line-height: 1.7; color: rgba(255,255,255,0.42);
        }
        .cmp-sources a { color: rgba(255,255,255,0.6); }
        @media (max-width: 620px) {
          /* Fit all three columns on a phone. The min-width above pushed the
             Splitwise column ~190px off the right edge at 375px, so the one
             thing this table exists to show — where each feature sits in each
             app — was invisible unless you happened to swipe a table that gave
             no sign it could scroll. Tighter type and padding beats a sideways
             scroll here; percentage widths stop the feature names from
             squeezing the two value columns to nothing. */
          .cmp-table { min-width: 0; font-size: 12.5px; table-layout: fixed; }
          .cmp-table th, .cmp-table td { padding: 10px 6px; }
          .cmp-table thead th { font-size: 9.5px; letter-spacing: 0.03em; }
          .cmp-table th:first-child, .cmp-table td:first-child { padding-left: 2px; }
          .cmp-table thead th:nth-child(1) { width: 44%; }
          .cmp-table thead th:nth-child(2) { width: 26%; }
          .cmp-table thead th:nth-child(3) { width: 30%; }
        }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a1538 0%, #0c1740 15%, #0e1d48 35%, #102650 50%, #0e1d48 70%, #0c1740 85%, #0a1538 100%)',
        color: 'white',
        fontFamily: font,
      }}>
        <PageHeader />

        <div className="cmp-wrap">
          <h1 className="cmp-h1">
            A free Splitwise alternative that reads the receipt for you
          </h1>

          <p className="cmp-lede">
            Splitwise is a good ledger. You tell it what everyone owes, and it keeps track.
            Tabster starts a step earlier: photograph the receipt and every line item comes
            back priced, so your friends can tap what they ordered instead of anyone typing
            the meal in by hand.
          </p>
          <p className="cmp-lede">
            Both are free to start. The difference is what you get without paying.
          </p>

          <div className="cmp-cta">
            <a className="cmp-btn" href="/receipts">Split a receipt free</a>
            <a className="cmp-btn cmp-btn-alt" href="/trips">Start a group trip</a>
          </div>

          <h2 className="cmp-h2">What each app gives you for free</h2>
          <p className="cmp-p">
            Splitwise offers receipt scanning and itemization — they are listed as Pro
            features on its own pricing page. In Tabster they are the product, and they are
            free. Here is where each feature sits:
          </p>

          <div className="cmp-table-scroll">
            <table className="cmp-table">
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  <th scope="col">Tabster</th>
                  <th scope="col">Splitwise</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((r) => (
                  <tr key={r.feature} className={r.highlight ? 'cmp-row-hi' : undefined}>
                    <th scope="row" style={{ fontWeight: 600, color: 'white' }}>{r.feature}</th>
                    <td className={r.tabsterYes ? 'cmp-yes' : undefined}>{r.tabster}</td>
                    <td className={r.splitwiseYes ? 'cmp-yes' : undefined}>{r.splitwise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Showing the itemization rather than asserting it. Most readers skim
              the headings and the table; a 50-second clip of the actual thing
              is the one asset the listicles competing for this query can't
              match. preload="none" + poster keeps it free until it's watched —
              the poster is 24KB, the clip 2.9MB and only fetched on play.

              The demo owns its own h2 rather than sitting under the cards'
              heading — it's a section in its own right, and an h3 here would
              have read as a subsection of the pricing table above it. */}
          <div className="cmp-demo">
            <div className="cmp-demo-copy">
              <h2 className="cmp-h2">Watch a bill get split with Tabster</h2>
              <p>
                Fifty seconds, start to finish: the receipt is photographed, every item
                comes back priced, the group claims what they ordered, and it ends in a
                Venmo request. Nobody has to type the bill in unless you don’t have a photo
                or link of the receipt.
              </p>
              {/* The whole row is the button, not just the timestamp — a 34px
                  wide number is under the 44px minimum tap target, and there is
                  no reason the label should not be clickable too. */}
              <ol className="cmp-beats">
                {BEATS.map((b) => (
                  <li key={b.t}>
                    <button
                      type="button"
                      className="cmp-beat"
                      onClick={() => seekTo(b.t)}
                      aria-label={`Play from ${b.at} — ${b.label}`}
                    >
                      <span className="cmp-beat-time">{b.at}</span>
                      <span>{b.label}</span>
                    </button>
                  </li>
                ))}
              </ol>
            </div>

            <video
              ref={demoRef}
              className="cmp-demo-video"
              src="/faq-videos/splitting-a-tab.mp4"
              poster="/faq-videos/splitting-a-tab-poster.jpg"
              width={1080}
              height={1920}
              controls
              playsInline
              muted
              loop
              preload="none"
              controlsList="nodownload"
            />
          </div>

          <h2 className="cmp-h2">What Tabster does differently</h2>
          <div className="cmp-grid">
            {TABSTER_DIFFERENCES.map((d) => (
              <div className="cmp-card" key={d.title}>
                <h3>
                  <span className="cmp-card-emoji" aria-hidden="true">{d.emoji}</span>
                  {d.title}
                </h3>
                <p>{d.body}</p>
              </div>
            ))}
          </div>

          {/* Deliberately AFTER the case above, not before it. This used to be
              the second heading on the page, which meant anyone skimming the
              H2s hit a competitor recommendation before a single reason to
              stay. The candour is worth keeping — it's what makes the rest
              credible to someone actively comparing — but it belongs at the
              point where the argument has already been made. The heading is
              self-referring for the same reason: "When to use Splitwise" scans
              as an endorsement, and headings are most of what gets read. */}
          <h2 className="cmp-h2">{notDoHeading}</h2>
          <p className="cmp-p">
            Worth being straight about, because for some use cases Splitwise makes more
            sense. It advertises these on its free tier:
          </p>
          <ul className="cmp-p" style={{ paddingLeft: '20px' }}>
            {SPLITWISE_STRENGTHS.map((s) => <li key={s} style={{ marginBottom: '6px' }}>{s}</li>)}
          </ul>
          <p className="cmp-p">
            If you are looking for a bill splitting app that helps you with expense
            categorization, or to use with friends who speak different languages, Splitwise
            is the better option. If you are looking to easily split a dinner, a weekend
            away, or the monthly bills with roommates, read on.
          </p>

          <h2 className="cmp-h2">Switching over</h2>
          <p className="cmp-p">
            There is nothing to migrate and nothing to cancel. Split your next bill in
            Tabster and see how it goes — you do not need an account to try it, and neither
            do the people you are splitting with.
          </p>
          <p className="cmp-p">
            New to it? Start with{' '}
            <a className="cmp-link" href="/support/getting-started/splitting-your-first-restaurant-bill">
              how to split a restaurant bill with friends
            </a>{' '}
            or{' '}
            <a className="cmp-link" href="/support/getting-started/splitting-your-first-group-trip">
              how to split expenses on a group trip
            </a>.
          </p>

          <div className="cmp-cta">
            <a className="cmp-btn" href="/receipts">Split a receipt free</a>
            <a className="cmp-btn cmp-btn-alt" href="/download">Get the app</a>
          </div>

          {/* Showing the sources and the date is what separates a comparison
              readers can trust from marketing. It also ages honestly: a stale
              date tells the reader to double-check rather than quietly
              misleading them. */}
          <p className="cmp-sources">
            Splitwise feature tiers above are quoted from Splitwise’s own pages, checked on {checkedOn}:{' '}
            {SOURCES.map((s, i) => (
              <React.Fragment key={s.url}>
                {i > 0 && ', '}
                <a href={s.url} rel="nofollow noopener noreferrer" target="_blank">{s.label}</a>
              </React.Fragment>
            ))}
            . Splitwise is a trademark of its owner; Tabster is not affiliated with or
            endorsed by Splitwise. Features and pricing change — check their current pages
            before deciding.
          </p>
        </div>

        <PageFooter />
      </div>
    </>
  );
};

export default SplitwiseAlternativeContent;
